import React, { useEffect, useState, useRef, useCallback, memo } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useRegion, priceFor } from '../../context/RegionContext';
import api from '../../utils/api';
import { SEO_PAGES } from '../Seo/seoPages';
import useSeo from '../Seo/useSeo';
import { regionSeo } from '../Seo/regionSeo';
import './Landing.css';

/* ═══════ Lazy template loaders ═══════
 * Each template weighs 10–40 KB and the landing only needs them when the
 * user is about to see a design preview. We avoid pulling TEMPLATE_REGISTRY
 * statically so the initial bundle stays lean. */
const TEMPLATE_LOADERS = {
  BoldEditorial: () => import('../../templates/BoldEditorial/BoldEditorial'),
  Sakura:        () => import('../../templates/Sakura/Sakura'),
  Provence:      () => import('../../templates/Provence/Provence'),
  Botanica:      () => import('../../templates/Botanica/Botanica'),
  Velvet:        () => import('../../templates/Velvet/Velvet'),
  Arctic:        () => import('../../templates/Arctic/Arctic'),
  Gatsby:        () => import('../../templates/Gatsby/Gatsby'),
  Aquarelle:     () => import('../../templates/Aquarelle/Aquarelle'),
  Ivory:         () => import('../../templates/Ivory/Ivory'),
  Rustico:       () => import('../../templates/Rustico/Rustico'),
  Cosmos:        () => import('../../templates/Cosmos/Cosmos'),
  Ultramarin:    () => import('../../templates/Ultramarin/Ultramarin'),
  Graphite:      () => import('../../templates/Graphite/Graphite'),
  Amethyst:      () => import('../../templates/Amethyst/Amethyst'),
  Organica:      () => import('../../templates/Organica/Organica'),
  Amorika:       () => import('../../templates/Amorika/Amorika'),
  Voyage:        () => import('../../templates/Voyage/Voyage'),
  Lemon:         () => import('../../templates/Lemon/Lemon'),
  Alarus:        () => import('../../templates/Alarus/Alarus'),
  Rustic:        () => import('../../templates/Rustic/Rustic'),
  OnaSkazalaDa:  () => import('../../templates/OnaSkazalaDa/OnaSkazalaDa'),
  Flora:         () => import('../../templates/Flora/Flora'),
};

const _templateCache = {};
const loadTemplate = (slug) => {
  if (_templateCache[slug]) return _templateCache[slug];
  const loader = TEMPLATE_LOADERS[slug];
  if (!loader) return Promise.resolve(null);
  _templateCache[slug] = loader().then(m => m.default).catch(() => null);
  return _templateCache[slug];
};

/* ═══════ Icon helper — loads local SVGs from /landing/icons/ ═══════ */
const IC = '/landing/icons/';
const Icon = ({ name, size = 24 }) => (
  <img src={`${IC}${name}.svg`} alt="" width={size} height={size} loading="lazy" decoding="async" style={{display:'block'}} />
);
const Heart = ({ s = 24 }) => <Icon name="heart" size={s} />;
const Rocket = ({ s = 24 }) => <Icon name="rocket" size={s} />;
const MapPin = ({ s = 32 }) => <Icon name="map-pin" size={s} />;
const Calendar = ({ s = 32 }) => <Icon name="calendar" size={s} />;
const Timer = ({ s = 32 }) => <Icon name="timer" size={s} />;
const Palette = ({ s = 32 }) => <Icon name="palette" size={s} />;
const Checks = ({ s = 32 }) => <Icon name="checks" size={s} />;
const Cheers = ({ s = 32 }) => <Icon name="cheers" size={s} />;
const ListChecks = ({ s = 32 }) => <Icon name="list-checks" size={s} />;
const MapTri = ({ s = 32 }) => <Icon name="map-trifold" size={s} />;
const ShootStar = ({ s = 24 }) => <Icon name="shooting-star" size={s} />;
const Question = ({ s = 24 }) => <Icon name="question" size={s} />;
const Butterfly = ({ s = 24 }) => <Icon name="butterfly" size={s} />;
const Money = ({ s = 24 }) => <Icon name="money" size={s} />;
const Star = ({ s = 20 }) => <Icon name="star" size={s} />;
const CheckCircle = ({ s = 24 }) => <Icon name="checks" size={s} />;
const Eyes = ({ s = 20 }) => <Icon name="eyes" size={s} />;
const ArrowR = ({ s = 20 }) => <Icon name="arrow-right" size={s} />;
const TgIcon = ({ s = 32 }) => <Icon name="telegram" size={s} />;
const MaxIcon = ({ s = 32 }) => <Icon name="max-logo" size={s} />;
const SmsIcon = ({ s = 32 }) => <Icon name="wechat" size={s} />;
const MailIcon = ({ s = 32 }) => <Icon name="mailbox" size={s} />;

/* Step arrow SVG */
const StepArrow = () => (
  <svg className="lp-step-arrow" width="111" height="37" viewBox="0 0 111 37" fill="none">
    <path d="M2 35C20 5 50 2 70 10C90 18 100 8 109 2" stroke="#FF005C" strokeWidth="1.5" strokeDasharray="6 4" strokeLinecap="round" fill="none"/>
    <path d="M103 6L109 2L105 8" stroke="#FF005C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
  </svg>
);

/* Logo component (local SVG from Figma) */
const Logo = () => <img src="/logo-amorika.svg" alt="amorika" className="lp-logo-img" width="285" height="60" decoding="async" />;

/* ═══════ Static data ═══════ */
const BLOCKS = [
  { I: MapPin,     n: 'Меню навигации',        d: 'Верхнее меню свадебного сайта с якорными ссылками' },
  { I: Calendar,   n: 'Добавление в календарь', d: 'Возможность добавить свадьбу в Google или Apple calendar' },
  { I: Timer,      n: 'Обратный отсчёт',        d: 'Таймер обратного отсчёта до даты свадьбы' },
  { I: Palette,    n: 'Палитра',                d: 'Цвета свадьбы и дресс-код можно настроить для гостей' },
  { I: Checks,     n: 'Подтверждение',          d: 'Кнопка подтверждения для гостей, для опроса присутствия' },
  { I: Cheers,     n: 'Опрос гостей',           d: 'Анкета опроса гостей для уточнения нюансов' },
  { I: ListChecks, n: 'Расписание',             d: 'Настройка расписания свадебного торжества' },
  { I: MapTri,     n: 'Карта',                  d: 'С адресом месторасположения важных локаций' },
  { I: ShootStar,  n: 'Виш лист',               d: 'Список желаемых подарков с возможностью резерва' },
];
const STEPS = [
  { num: 'Шаг 1',     t: 'Выберите дизайн',    d: 'Выберите один из стильных шаблонов в нужном вам стиле' },
  { num: 'Шаг 2',     t: 'Заполните данные',    d: 'Имена, дата, место и фото — все просто и быстро' },
  { num: 'Шаг 3',     t: 'Настройте блоки',     d: 'Выберите нужные разделы: карта, подтверждение, расписание, виш-лист' },
  { num: 'Результат',  t: 'Получите результат',  d: 'Готовый сайт приглашение для ваших гостей', accent: true },
];
const REVIEWS_DATA = [
  { n: 'Екатерина и Дмитрий', c: 'Санкт-Петербург', dt: 'Июнь 2025',     i: 'Е', t: 'Собрали сайт за вечер. Бабушки и дедушки разобрались — это лучший комплимент. Подтверждения летели сразу после рассылки.' },
  { n: 'Полина и Артём',      c: 'Казань',          dt: 'Сентябрь 2025', i: 'П', t: 'Чат-бот вёл по шагам, не запутаешься. Гостям отправили одну ссылку — и сразу видели, кто придёт, и что заказать на банкет.' },
  { n: 'Марина и Илья',       c: 'Екатеринбург',    dt: 'Август 2025',   i: 'М', t: 'Понравилось, что можно поменять шаблон в один клик и текст не теряется. Выбрали «Лимон», получилось очень нежно.' },
  { n: 'София и Артур',       c: 'Нижний Новгород', dt: 'Декабрь 2025',  i: 'С', t: 'Без дизайнера, без вёрстки — за час подняли красивый сайт. Гости писали, что таких приглашений ещё не видели.' },
  { n: 'Алина и Кирилл',      c: 'Краснодар',       dt: 'Февраль 2026',  i: 'А', t: 'Особенно зашёл блок с дресс-кодом и палитрой — гости заранее понимали, в чём приходить. Никто не промахнулся по стилю.' },
  { n: 'Дарья и Никита',      c: 'Новосибирск',     dt: 'Апрель 2026',   i: 'Д', t: 'Поделились ссылкой в Telegram — все подтвердили за два дня. Удобно, что видно сразу всю таблицу гостей в кабинете.' },
];
const FEATURES = ['Все шаблоны','Все блоки','Личный кабинет','Доступ на 1 год','Уникальные ссылки','Точечная настройка','Подтверждения гостей'];
const CATS = [{ k:'all', l:'Все стили' },{ k:'modern', l:'Модерн' },{ k:'romantic', l:'Романтика' },{ k:'rustic', l:'Рустик' }];
const MOCK = [
  { id:2, name:'Яркий редакционный',      category:'modern', template_slug:'BoldEditorial',    tags:['Модерн','Смелый'],      config:{primaryColor:'#FF1654',secondaryColor:'#FFFFFF'} },
  { id:3, name:'Графит',                  category:'modern', template_slug:'Graphite',         tags:['Минимализм','Тёмный'],   config:{primaryColor:'#000000',secondaryColor:'#ffffff'} },
  { id:4, name:'Путешествие',             category:'modern', template_slug:'Voyage',           tags:['Лавандовый','Travel'],   config:{primaryColor:'#6579FF',secondaryColor:'#FFFFFF'} },
  { id:5, name:'Флора',                   category:'rustic', template_slug:'Flora',            tags:['Ботаника','Акварель'],   config:{primaryColor:'#2B3C2C',secondaryColor:'#ECF2E6'} },
];

/* ═══════ Isolated iframe renderer — prevents LP styles from leaking ═══════ */
const IframeTemplate = memo(function IframeTemplate({ T, project, design, scaleClass }) {
  const frameRef = useRef(null);
  const [portalTarget, setPortalTarget] = useState(null);

  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) return;

    const init = () => {
      const doc = frame.contentDocument;
      if (!doc) return;
      doc.open();
      doc.write('<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"/></head><body style="margin:0;padding:0;overflow:hidden;"></body></html>');
      doc.close();
      // Copy all injected <style> tags from the parent document
      document.querySelectorAll('style').forEach(s => {
        doc.head.appendChild(s.cloneNode(true));
      });
      // Copy any <link rel="stylesheet"> (production builds)
      document.querySelectorAll('link[rel="stylesheet"]').forEach(l => {
        const copy = doc.createElement('link');
        copy.rel = 'stylesheet'; copy.href = l.href;
        doc.head.appendChild(copy);
      });
      setPortalTarget(doc.body);
    };

    if (frame.contentDocument?.readyState === 'complete') init();
    else frame.addEventListener('load', init, { once: true });
  }, []);

  return (
    <>
      <iframe
        ref={frameRef}
        title="template-preview"
        style={{ border: 'none', display: 'block', width: '100%', height: '100%' }}
      />
      {portalTarget && createPortal(
        <div className={scaleClass}>
          <T project={project} design={design} isPreview={true} />
        </div>,
        portalTarget
      )}
    </>
  );
});

/* ═══════ Device preview ═══════
   Uses the admin-uploaded thumbnail_url when present (WebP screenshot of the
   template). Falls back to a gradient placeholder based on the design's
   palette if no thumbnail has been uploaded yet. */
const DesignDevices = memo(function DesignDevices({ d }) {
  if (d.thumbnail_url) {
    return (
      <div className="lp-dcard-devices lp-dcard-devices--img">
        <img
          src={d.thumbnail_url}
          alt={d.name}
          className="lp-dcard-thumb"
          loading="lazy"
          decoding="async"
        />
      </div>
    );
  }
  return (
    <div
      className="lp-dcard-devices lp-dcard-devices--ph"
      style={{ background: `linear-gradient(135deg,${d.config?.primaryColor||'#F5C6D0'} 0%,${d.config?.secondaryColor||'#FFE8EE'} 100%)` }}
    />
  );
});

/* Компактный переключатель региона (дропдаун). Опции — реальные <a href>
   на /, /kz, /by, всегда в DOM (скрыты visibility/opacity, не display:none),
   чтобы поисковые роботы видели ссылки. onClick фиксирует выбор в
   localStorage — иначе «/» (RU) подхватил бы прежний «липкий» регион. */
const REGION_OPTS = [
  { r: 'ru', href: '/',   label: '🇷🇺 RU' },
  { r: 'kz', href: '/kz', label: '🇰🇿 KZ' },
  { r: 'by', href: '/by', label: '🇧🇾 BY' },
];
function RegionDropdown({ region }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const cur = REGION_OPTS.find(o => o.r === region) || REGION_OPTS[0];
  useEffect(() => {
    const onDoc = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('click', onDoc);
    return () => document.removeEventListener('click', onDoc);
  }, []);
  const pick = r => {
    try { localStorage.setItem('region', r); } catch (e) { /* ignore */ }
    if (!user) return;
    /* Залогинен — сохраняем регион в БД. keepalive, чтобы запрос пережил
       полный переход по ссылке. Плюс обновляем локальный кэш user. */
    try {
      const token = localStorage.getItem('token');
      fetch('/api/auth/region', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ region: r }),
        keepalive: true,
      }).catch(() => {});
      const u = JSON.parse(localStorage.getItem('user') || 'null');
      if (u) { u.region = r; localStorage.setItem('user', JSON.stringify(u)); }
    } catch (e) { /* ignore */ }
  };
  return (
    <div className={`lp-region-dd${open ? ' open' : ''}`} ref={ref}>
      <button type="button" className="lp-region-trigger" aria-haspopup="listbox" aria-expanded={open}
        onClick={() => setOpen(o => !o)}>
        {cur.label} <span className="lp-region-caret">▾</span>
      </button>
      <div className="lp-region-menu" role="listbox">
        {REGION_OPTS.map(o => (
          <a key={o.r} href={o.href} onClick={() => pick(o.r)}
            className={`lp-region-item${o.r === region ? ' active' : ''}`}>{o.label}</a>
        ))}
      </div>
    </div>
  );
}

/* ═══════ Component ═══════ */
export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  /* For an authenticated user EVERY link on the landing page should take
     them straight to their dashboard — no marketing detours, no register
     funnel. Delegated capture-phase handler on the page root: intercept
     any <a> click (React Router <Link> also renders an <a>) and redirect
     to /dashboard. Guests are untouched — links work normally for them.
     Capture phase + stopPropagation also neutralises any per-link
     onClick so it can't override the redirect. */
  const handleLandingClick = (e) => {
    if (!user) return;
    const a = e.target.closest && e.target.closest('a');
    if (!a) return;                       // not a link (button/filter/etc.)
    if (a.target === '_blank') return;    // leave explicit new-tab links
    if (a.classList.contains('lp-region-item')) return;  // переключатель региона — не уводим в ЛК
    const href = a.getAttribute('href') || '';
    /* Не уводим в ЛК: якоря (скролл по странице), страницы документов,
       а также внешние ссылки и контакты (почта/телефон/Telegram). */
    if (href.startsWith('#')) return;
    if (href === '/privacy' || href === '/offer') return;
    if (href === '/blog' || href.startsWith('/blog/')) return;  // блог — публичный
    if (SEO_PAGES.some(p => href === `/${p.slug}`)) return;  // SEO-посадочные — публичные
    if (href.startsWith('mailto:') || href.startsWith('tel:') || /^https?:\/\//.test(href)) return;
    e.preventDefault();
    e.stopPropagation();
    navigate('/dashboard');
  };
  const [designs, setDesigns] = useState(MOCK);
  const [cat, setCat] = useState('all');
  const [preview, setPreview] = useState(null);
  const [adminDefaults, setAdminDefaults] = useState(null);
  const { region } = useRegion();
  const rp = priceFor(adminDefaults?.pricing, region); // { price, originalPrice, symbol, locale }
  /* Региональное SEO (/, /by, /kz): title/description/canonical + hreflang. */
  const seo = regionSeo(region);
  useSeo(seo);
  const [PreviewTpl, setPreviewTpl] = useState(null);
  /* Social-proof counter — same source as the chatbot payment step so
     the number stays consistent across the funnel. Server returns the
     real count of created sites; we add a +1000 baseline (early-stage
     growth) so the displayed figure matches `cb-pub-social`. */
  const [pairsCount, setPairsCount] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get('/projects/stats/social')
      .then(r => { if (alive) setPairsCount((r?.data?.count || 0) + 1000); })
      .catch(() => { /* silent — falls back to default label */ });
    return () => { alive = false; };
  }, []);
  const pairsLabel = pairsCount != null ? pairsCount.toLocaleString('ru-RU') : '1 000 +';
  const modalRef = useRef(null);
  const previewWrapRef = useRef(null);

  const scalePreview = useCallback(() => {}, []);

  useEffect(() => {
    if (!preview) { setPreviewTpl(null); return; }
    const timer = setTimeout(scalePreview, 50);
    window.addEventListener('resize', scalePreview);
    /* Load the selected template (and a fallback) lazily when the modal opens. */
    let cancelled = false;
    loadTemplate(preview.template_slug).then(C => {
      if (cancelled) return;
      if (C) setPreviewTpl(() => C);
      else loadTemplate('BoldEditorial').then(F => { if (!cancelled && F) setPreviewTpl(() => F); });
    });
    return () => { cancelled = true; clearTimeout(timer); window.removeEventListener('resize', scalePreview); };
  }, [preview, scalePreview]);

  /* Defer network calls until the browser is idle so they don't compete with
     hero render / LCP. Falls back to a 1s timer if requestIdleCallback is
     unavailable (Safari). */
  useEffect(() => {
    const schedule = (cb) => (window.requestIdleCallback
      ? window.requestIdleCallback(cb, { timeout: 2000 })
      : setTimeout(cb, 1000));
    const cancel = (h) => (window.cancelIdleCallback ? window.cancelIdleCallback(h) : clearTimeout(h));
    const h = schedule(() => {
      api.get('/designs').then(r => { if (Array.isArray(r.data)) setDesigns(r.data); }).catch(() => {});
      api.get('/admin/defaults').then(r => setAdminDefaults(r.data)).catch(() => {});
    });
    return () => cancel(h);
  }, []);

  /* Прокрутка к якорю при заходе с хэшем (#designs и т.п.).
     Контент главной догружается асинхронно, поэтому браузер сам к нужному
     блоку не доезжает — скроллим вручную, повторяя попытки, пока элемент
     не появится и его позиция не стабилизируется. */
  useEffect(() => {
    const hash = window.location.hash;
    if (!hash || hash.length < 2) return;
    const id = decodeURIComponent(hash.slice(1));
    let tries = 0;
    let lastTop = null;
    let timer;
    const tick = () => {
      const el = document.getElementById(id);
      if (el) {
        const top = Math.round(el.getBoundingClientRect().top);
        el.scrollIntoView({ behavior: 'auto', block: 'start' });
        /* Останавливаемся, когда позиция блока перестала «прыгать»
           (контент над ним догрузился) или исчерпали попытки. */
        if (lastTop !== null && Math.abs(top - lastTop) < 4) return;
        lastTop = top;
      }
      if (tries++ < 20) timer = setTimeout(tick, 120);
    };
    timer = setTimeout(tick, 120);
    return () => clearTimeout(timer);
  }, []);

  /* Scroll animations via IntersectionObserver */
  useEffect(() => {
    const els = document.querySelectorAll('.lp-anim-target, .lp-anim-stagger');
    if (!els.length) return;
    const obs = new IntersectionObserver((entries) => {
      entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('lp-visible'); obs.unobserve(e.target); } });
    }, { threshold: 0.15 });
    els.forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  const demoProject = useCallback((d) => {
    const def = adminDefaults || {};
    const bride = def.bride || 'Анна';
    const groom = def.groom || 'Михаил';
    const bc = def.block_content || {};
    return {
      bride, groom,
      wedding_date: new Date(Date.now()+86400000*90).toISOString().slice(0,10),
      wedding_place: def.wedding_place || 'Москва',
      wedding_address: def.wedding_address || 'Банкетный зал «Эдем», ул. Садовая, 12',
      wedding_slogan: def.wedding_slogan || 'Две судьбы — одна история',
      invitation_text: def.invitation_text || '',
      blocks: def.blocks || ['menu','countdown','schedule','map','palette','rsvp','survey','message','wishlist','calendar'],
      block_content: {
        menu: bc.menu || { title: `${bride} & ${groom}` },
        calendar: bc.calendar || { title: 'Добавить в календарь' },
        countdown: bc.countdown || { title: 'До нашей свадьбы', subtitle: '' },
        palette: bc.palette || { title: 'Дресс-код', dresscode: '' },
        rsvp: bc.rsvp || { title: 'Подтвердите присутствие', subtitle: '', yesText: 'Приду!', noText: 'Не смогу', btnText: 'Заполнить анкету' },
        message: bc.message || { title: 'Ваши пожелания', text: '' },
        survey: bc.survey || { title: 'Анкета гостя' },
        schedule: bc.schedule || { title: 'Тайминг', subtitle: '' },
        map: bc.map || { title: 'Локация', subtitle: '' },
        wishlist: bc.wishlist || { title: 'Что дарить', subtitle: '' },
        couple: bc.couple || { title: 'Наша история', brideRole: 'Невеста', groomRole: 'Жених' },
        story: bc.story || { items: [] },
      },
      palette: def.palette
        ? { ...def.palette, primary: d.config?.primaryColor || def.palette.primary, secondary: d.config?.secondaryColor || def.palette.secondary, accent: d.config?.accentColor || def.palette.accent }
        : { primary: d.config?.primaryColor||'#C4956A', secondary: d.config?.secondaryColor||'#FDF8F3', accent: d.config?.accentColor||'#E8C5A0', textDark: '#3D3530', textLight: '#FFF' },
      fonts: { title: d.config?.fontTitle||'Playfair Display', body: d.config?.fontBody||'Lato' },
      venues: [
        { id:1, type:'ceremony', label:'Церемония', name:'Зал «Эдем»', address:'Москва, Садовая 12' },
        { id:2, type:'reception', label:'Банкет', name:'Ресторан «Верандо»', address:'Москва, Кутузовский 5' },
      ],
      cover_photos: def.cover_photos || [],
      cover_photo: def.cover_photo || null,
      dresscode_palette: def.dresscode_palette || [{ color:'#F1EDE6', label:'Кремовый' }],
      schedule: def.schedule || [{ time:'13:00', event:'Сбор гостей' },{ time:'14:00', event:'Церемония' },{ time:'16:00', event:'Банкет' }],
      survey: def.survey || [{ id:'1', question:'Что пить?', type:'single', options:['Алкоголь','Безалкогольное'] }],
      wishlist: def.wishlist || [
        { id: 'w1', name: 'Набор постельного белья', price: '12000', link: '' },
        { id: 'w2', name: 'Кофемашина',              price: '35000', link: '' },
        { id: 'w3', name: 'Сертификат на путешествие', price: '50000', link: '' },
      ],
      template_slug: d.template_slug || 'default',
      payment_plan: 'demo',
    };
  }, [adminDefaults]);

  const safeDesigns = Array.isArray(designs) ? designs : MOCK;
  const list = cat === 'all' ? safeDesigns : safeDesigns.filter(d => d.category === cat);

  return (
    <div className="lp" onClickCapture={handleLandingClick}>

      {/* ── Modal (portal — outside .lp to avoid style resets) ── */}
      {preview && createPortal(
        <div className="lp-modal-bg" onClick={() => setPreview(null)}>
          <div className="lp-modal" ref={modalRef} onClick={e => e.stopPropagation()}>
            <div className="lp-modal-top">
              <span>Превью: {preview.name}</span>
              <button type="button" onClick={() => setPreview(null)}>✕</button>
            </div>
            <div className="lp-modal-scroll">
              {/* `lp-phone-screen` activates templates' built-in preview-sandbox
                  overrides (see *.css `.lp-phone-screen .X` rules) so desktop
                  @media (min-width: 1024px) layouts don't leak into the narrow
                  440px modal when the host window is wide. */}
              <div className="lp-modal-preview-wrap lp-phone-screen" ref={previewWrapRef}>
                {PreviewTpl ? <PreviewTpl project={demoProject(preview)} design={{config:preview.config||{}}} isPreview /> : <div className="lp-modal-loader">Загрузка…</div>}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ═══ 1. HEADER ═══ */}
      <header className="lp-header">
        <div className="lp-header-left">
          <Link to="/" className="lp-header-logo"><Logo /></Link>
          <nav className="lp-header-nav">
            <a href="#features">Возможности</a>
            <a href="#designs">Дизайн</a>
            <a href="#pricing">Цены</a>
            <a href="#how">Как это работает</a>
            <Link to="/blog">Блог</Link>
          </nav>
        </div>
        <div className="lp-header-right">
          <RegionDropdown region={region} />
          <Link to={user ? '/dashboard' : '/login'} className="lp-hbtn lp-hbtn--ghost">Мои проекты</Link>
          <Link to={user ? '/dashboard' : '/login'} className="lp-hbtn lp-hbtn--pink">{user ? 'Кабинет' : 'Войти'}</Link>
        </div>
      </header>

      <main>
      {/* ═══ 2. HERO ═══ */}
      <section className="lp-hero-wrap">
        <div className="lp-hero-card" style={{backgroundImage: 'var(--lp-hero-bg)'}}>
          <div className="lp-hero-body">
            <div className="lp-badge"><Heart /> За 5 минут <Heart /></div>
            <h1 className="lp-hero-title">Сайт-приглашение на свадьбу, на которое отвечают <span className="lp-pink">«приду»</span> с первого взгляда</h1>
            <p className="lp-hero-sub">Яркая анимации, ваши фото, обратный отсчёт. Гости подтверждают участие в одно касание.</p>
            {seo.heroNote && <p className="lp-hero-region">📍 {seo.heroNote}</p>}
            <div className="lp-hero-btns">
              <Link to="/register" className="lp-btn lp-btn--pink">Создать пригласительное</Link>
            </div>
          </div>
        </div>
        <div className="lp-stats">
          <div className="lp-stat"><span className="lp-stat-num">{pairsLabel}</span><span className="lp-stat-lbl">пар воспользовались</span></div>
          <div className="lp-stat lp-stat--grow"><span className="lp-stat-num">5 минут</span><span className="lp-stat-lbl">на создание</span></div>
          <div className="lp-stat"><span className="lp-stat-num">10</span><span className="lp-stat-lbl">шаблонов дизайна</span></div>
        </div>
      </section>

      {/* ═══ 3. PERSONALIZE ═══ */}
      <section className="lp-section" id="features">
        <div className="lp-container">
          <div className="lp-section-head lp-anim-target" style={{marginBottom:0}}>
            <h2 className="lp-h2">Персонализируйте любой шаблон под ваш стиль</h2>
            <p className="lp-sub">Красивый персональный сайт с уникальной ссылкой для каждого гостя</p>
          </div>
          <div className="lp-pers-badges lp-anim-target">
            <span className="lp-badge lp-badge--white"><Heart /> Уникальные шрифты</span>
            <span className="lp-badge lp-badge--white"><Heart /> Индивидуальная палитра</span>
            <span className="lp-badge lp-badge--white"><Heart /> Ваши фото</span>
          </div>
          <div className="lp-mockups lp-anim-target">
            {/* Phone mockup */}
            <div className="lp-device lp-device--phone">
              <div className="lp-device-screen">
                <picture>
                  <source srcSet="/landing/mockup-phone.webp?v=20260509" type="image/webp" />
                  <img src="/landing/mockup-phone.png?v=20260509" alt="Мобильный шаблон" className="lp-device-scroll" loading="lazy" decoding="async" width="390" height="5489" />
                </picture>
              </div>
            </div>
            {/* Desktop mockup */}
            <div className="lp-device lp-device--desktop">
              <div className="lp-device-screen">
                <picture>
                  <source srcSet="/landing/mockup-desktop.webp?v=20260509" type="image/webp" />
                  <img src="/landing/mockup-desktop.png?v=20260509" alt="Десктопный шаблон" className="lp-device-scroll" loading="lazy" decoding="async" width="1200" height="4281" />
                </picture>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ 4. BLOCKS ═══ */}
      <section className="lp-section" id="blocks">
        <div className="lp-wide">
          <div className="lp-section-head lp-anim-target">
            <div className="lp-badge"><Rocket /> Все что нужно</div>
            <h2 className="lp-h2">Доступные блоки</h2>
          </div>
          <div className="lp-blocks-grid">
            {BLOCKS.map((b,i) => <div className="lp-bcard" key={i}><b.I /><div className="lp-bcard-info"><div className="lp-bcard-name">{b.n}</div><div className="lp-bcard-desc">{b.d}</div></div></div>)}
          </div>
          <div className="lp-center" style={{marginTop:16}}><Link to="/register" className="lp-btn lp-btn--pink">Сделать пригласительные</Link></div>
        </div>
      </section>

      {/* ═══ 5. HOW IT WORKS ═══ */}
      <section className="lp-section lp-how" id="how">
        <div className="lp-wide" style={{position:'relative'}}>
          <div className="lp-section-head lp-anim-target">
            <div className="lp-badge"><Question /> Как это работает</div>
            <h2 className="lp-h2">Быстрый и понятный конструктор</h2>
            <p className="lp-sub">Пошаговый мастер проведёт вас от выбора дизайна до готового приглашения</p>
          </div>
          <div className="lp-steps lp-anim-stagger">
            {STEPS.map(s => (
              <div className={`lp-step${s.accent ? ' lp-step--accent' : ''}`} key={s.num}>
                <div className="lp-step-num">{s.num}</div>
                <div className={`lp-step-title${s.accent ? ' lp-step-title--w' : ''}`}>{s.t}</div>
                <div className={`lp-step-desc${s.accent ? ' lp-step-desc--w' : ''}`}>{s.d}</div>
              </div>
            ))}
          </div>
          <img className="lp-step-arrow-1" src="/landing/step-arrow.svg" alt="" loading="lazy" decoding="async" aria-hidden="true" />
          <img className="lp-step-arrow-2" src="/landing/step-arrow.svg" alt="" loading="lazy" decoding="async" aria-hidden="true" />
          <img className="lp-step-wave" src="/landing/step-wave.svg" alt="" loading="lazy" decoding="async" aria-hidden="true" />
        </div>
      </section>


      {/* ═══ 6. DESIGNS ═══ */}
      <section className="lp-section" id="designs">
        <div className="lp-container">
          <div className="lp-section-head lp-anim-target">
            <div className="lp-badge"><Palette s={24} /> Готовые шаблоны</div>
            <h2 className="lp-h2">Выберите дизайн</h2>
            <p className="lp-sub">Все шаблоны адаптированы для мобильных устройств</p>
          </div>
          <div className="lp-filter">
            {CATS.map(c => <button type="button" key={c.k} className={`lp-fbtn${cat===c.k?' lp-fbtn--on':''}`} onClick={()=>setCat(c.k)}>{c.l}</button>)}
          </div>
        </div>
        <div className="lp-wide">
          <div className="lp-designs lp-anim-stagger">
            {list.map(d => (
              <div className="lp-dcard" key={d._id||d.id}>
                <div className="lp-dcard-img">
                  <DesignDevices d={d} demoProject={demoProject} adminDefaults={adminDefaults} />
                  <div className="lp-dcard-tags">{(d.tags||[]).slice(0,2).map(t => <span className="lp-dcard-tag" key={t}>{t}</span>)}</div>
                </div>
                <div className="lp-dcard-info">
                  <span className="lp-dcard-name">{d.name}</span>
                  <div className="lp-dcard-row">
                    <button type="button" className="lp-dcard-prev" onClick={()=>setPreview(d)}>Превью <Eyes /></button>
                    <Link to="/register" className="lp-dcard-setup">Настроить <ArrowR /></Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="lp-center" style={{marginTop:40}}><Link to="/register" className="lp-btn lp-btn--pink">Посмотреть все</Link></div>
        </div>
      </section>

      {/* ═══ 7. SHARE ═══ */}
      <section className="lp-section" id="share">
        <div className="lp-wide">
          <div className="lp-share-card">
            <div className="lp-section-head lp-anim-target">
              <div className="lp-badge"><Butterfly /> Поделитесь с гостями</div>
              <h2 className="lp-h2">Отправьте одним кликом</h2>
              <p className="lp-sub">Поделитесь с гостями в один клик — в Telegram, MAX, СМС или email</p>
            </div>
            <div className="lp-channels lp-anim-stagger">
              <div className="lp-channel" style={{background:'rgba(53,174,240,.1)'}}><TgIcon /><span style={{color:'#35AEF0'}}>Telegram</span></div>
              <div className="lp-channel" style={{background:'rgba(50,88,252,.1)'}}><MaxIcon /><span style={{color:'#3258FC'}}>Max</span></div>
              <div className="lp-channel" style={{background:'rgba(183,11,202,.1)'}}><SmsIcon /><span style={{color:'#EB00BC'}}>СМС</span></div>
              <div className="lp-channel" style={{background:'#FFF0F5'}}><MailIcon /><span style={{color:'#DB004F'}}>Email</span></div>
            </div>
            <p className="lp-share-etc">… или любой другой удобный мессенджер</p>
          </div>
        </div>
      </section>

      {/* ═══ 8. REVIEWS ═══ */}
      <section className="lp-section" id="reviews">
        <div className="lp-container">
          <div className="lp-section-head lp-anim-target">
            <div className="lp-badge"><ShootStar /> Отзывы</div>
            <h2 className="lp-h2">Что говорят пары</h2>
            <p className="lp-sub">Пошаговый мастер проведёт вас от выбора дизайна до готового приглашения</p>
          </div>
        </div>
        <div className="lp-wide">
          <div className="lp-reviews lp-anim-stagger">
            {REVIEWS_DATA.map((r,i) => (
              <div className="lp-review" key={i}>
                <div><div className="lp-review-stars">{[0,1,2,3,4].map(j=><Star key={j} />)}</div><p className="lp-review-text">{r.t}</p></div>
                <div className="lp-review-author">
                  <div className="lp-review-avatar">{r.i}</div>
                  <div><div className="lp-review-name">{r.n}</div><div className="lp-review-meta">{r.c} · {r.dt}</div></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ 9. PRICING ═══ */}
      <section className="lp-section lp-pricing-wrap" id="pricing">
        <img className="lp-pricing-bg" src="/landing/pricing-bg.svg" alt="" loading="lazy" decoding="async" aria-hidden="true" />
        <div className="lp-pricing-inner lp-container">
          <div className="lp-section-head lp-anim-target">
            <div className="lp-badge"><Money /> Стоимость</div>
            <h2 className="lp-h2 lp-h2--white">Единый тариф на все условия</h2>
            <p className="lp-sub lp-sub--white">Никаких скрытых платежей</p>
          </div>
          <div className="lp-price-card lp-anim-target">
            <div className="lp-price-left">
              <div className="lp-price-title">Сайт-приглашение</div>
              <div className="lp-price-feats">
                {FEATURES.map(f => <span className="lp-price-feat" key={f}><CheckCircle /> {f}</span>)}
              </div>
            </div>
            <div className="lp-price-right">
              <div>
                <div className="lp-price-row"><span className="lp-price-amount">{rp.price.toLocaleString(rp.locale)} {rp.symbol}</span><div><div className="lp-price-old">{rp.originalPrice.toLocaleString(rp.locale)} {rp.symbol}</div><div className="lp-price-note">За один сайт</div></div></div>
              </div>
              <Link to="/register" className="lp-btn lp-btn--pink" style={{width:'100%'}}>Сделать пригласительные</Link>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ 10. CTA ═══ */}
      <section className="lp-cta-wrap">
        <div className="lp-cta-card">
          <div className="lp-cta-body lp-anim-target">
            <h2 className="lp-cta-title">Создайте свои пригласительные прямо сейчас</h2>
            <p className="lp-cta-sub">Займёт не более 5 минут. Результат — красивый сайт пригласительное</p>
            <Link to="/register" className="lp-cta-btn">Сделать пригласительные</Link>
          </div>
        </div>
      </section>

      </main>

      {/* ═══ 11. FOOTER ═══ */}
      <footer className="lp-footer">
        <div className="lp-footer-inner">
          <div className="lp-footer-brand">
            <Logo />
            <p>Свадебный сайт пригласительное онлайн - быстро, красиво, без сложностей</p>
          </div>
          <div className="lp-footer-cols">
            <div className="lp-footer-col"><h3>Продукт</h3><a href="#features">Возможности</a><a href="#designs">Дизайны</a><a href="#pricing">Цены</a><Link to="/sozdat-priglashenie-na-svadbu">Создать приглашение онлайн</Link><Link to="/elektronnye-priglasheniya-na-svadbu">Электронные приглашения</Link><Link to="/svadebnye-priglasheniya-onlayn">Свадебные приглашения онлайн</Link><Link to="/konstruktor-svadebnyh-priglasheniy">Конструктор приглашений</Link></div>
            <div className="lp-footer-col"><h3>Аккаунт</h3><Link to="/register">Регистрация</Link><Link to="/login">Вход</Link><Link to="/dashboard">Мои проекты</Link><Link to="/blog">Блог</Link></div>
            <div className="lp-footer-col"><h3>Документы</h3><Link to="/privacy">Политика конфиденциальности</Link><Link to="/offer">Условия использования (оферта)</Link></div>
            <div className="lp-footer-col"><h3>Поддержка</h3>
              <a className="lp-footer-contact" href="mailto:mail@amorika.love">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true"><rect x="3" y="5" width="18" height="14" rx="2.5" stroke="currentColor" strokeWidth="1.7"/><path d="M4 7l8 6 8-6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/></svg>
                mail@amorika.love
              </a>
              <a className="lp-footer-contact" href="https://t.me/amorika_support_bot" target="_blank" rel="noopener noreferrer">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M21.5 4.3L2.9 11.5c-1 .4-1 1.8.1 2.1l4.6 1.4 1.8 5.4c.2.6 1 .8 1.5.3l2.5-2.4 4.6 3.4c.6.4 1.4.1 1.6-.6L22.9 5.6c.2-.9-.6-1.6-1.4-1.3z" fill="currentColor"/><path d="M8 14.5l9-6-7 7" fill="#fff" fillOpacity="0.55"/></svg>
                @amorika_support_bot
              </a>
            </div>
          </div>
        </div>
        <div className="lp-footer-legal">
          <span>СМЗ Савков Богдан Александрович · ИНН 771485708819</span>
        </div>
        <div className="lp-footer-copy">
          <span>© 2024 amorika. Все права защищены.</span>
        </div>
      </footer>
    </div>
  );
}
